/**
 * ManageMyData — calm parent/staff-facing data rights panel.
 *
 * Surfaces:
 * - Download my data (GET /api/users/me/inventory then trigger export)
 * - Schedule account delete with 30-day cooling-off
 * - Cancel during the cooling-off window
 */
import { useEffect, useState } from "react";
import { Download, Trash2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

function fmt(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString([], {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return "—";
  }
}

export default function ManageMyData({ testid = "manage-my-data" }) {
  const [me, setMe] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [requesting, setRequesting] = useState(false);

  const refresh = async () => {
    try {
      const r = await api.get("/auth/me");
      setMe(r.data);
    } catch {
      setMe(null);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const downloadExport = async () => {
    setExporting(true);
    try {
      const res = await api.get("/users/me/export", {
        responseType: "blob",
      });
      const blob = new Blob([res.data], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "alouvya-my-data.json";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast("Your archive is downloading.");
    } catch (err) {
      toast(err.response?.data?.detail || "Couldn't prepare the archive.");
    } finally {
      setExporting(false);
    }
  };

  const requestDelete = async () => {
    if (
      !confirm(
        "We'll hold your data for 30 quiet days before removing it. You can cancel during that time. Continue?"
      )
    )
      return;
    setRequesting(true);
    try {
      const r = await api.post("/users/me/data-delete-request");
      toast(r.data.message);
      refresh();
    } catch (err) {
      toast(err.response?.data?.detail || "Couldn't queue the request.");
    } finally {
      setRequesting(false);
    }
  };

  const cancelDelete = async () => {
    setRequesting(true);
    try {
      const r = await api.post("/users/me/data-delete-cancel");
      toast(r.data.message);
      refresh();
    } catch {
      toast("Couldn't cancel.");
    } finally {
      setRequesting(false);
    }
  };

  if (!me) return null;
  const deletionRequested = !!me.deletion_requested_at;

  return (
    <section
      data-testid={testid}
      className="rounded-3xl bg-white border hairline p-6"
    >
      <div className="flex items-baseline gap-3">
        <Sparkles size={14} strokeWidth={1.6} className="text-[#8A9984]" />
        <span className="eyebrow">Your data, your call</span>
      </div>
      <p className="mt-2 text-xs text-[#525252]">
        Calm controls. Download everything we hold about you, or ask us to
        gently forget. Either way: no friction.
      </p>

      {/* Export */}
      <div className="mt-5 flex items-center justify-between gap-3">
        <div>
          <p className="text-sm text-[#2C2B29] font-medium">Download my data</p>
          <p className="text-[11px] text-[#525252] mt-0.5">
            A JSON archive of your profile, children, messages, and history.
          </p>
        </div>
        <button
          type="button"
          onClick={downloadExport}
          disabled={exporting}
          data-testid={`${testid}-export`}
          className="inline-flex items-center gap-2 rounded-full px-4 py-2 bg-white border hairline text-xs tracking-[0.16em] uppercase font-medium text-[#525252] hover:bg-[#F2EFEA] disabled:opacity-50"
        >
          <Download size={13} strokeWidth={1.7} />
          {exporting ? "Preparing…" : "Download"}
        </button>
      </div>

      {/* Delete */}
      <div className="mt-6 flex items-center justify-between gap-3 pt-6 border-t border-[#171717]/8">
        <div>
          <p className="text-sm text-[#2C2B29] font-medium">
            {deletionRequested
              ? "Delete request is in flight"
              : "Ask us to forget"}
          </p>
          <p className="text-[11px] text-[#525252] mt-0.5">
            {deletionRequested
              ? `Requested · ${fmt(me.deletion_requested_at)}. We're holding for 30 quiet days.`
              : "30-day cooling-off, then your data is gently removed."}
          </p>
        </div>
        {deletionRequested ? (
          <button
            type="button"
            onClick={cancelDelete}
            disabled={requesting}
            data-testid={`${testid}-cancel-delete`}
            className="inline-flex items-center gap-2 rounded-full px-4 py-2 bg-[#1F3A2A]/10 text-[#1F3A2A] text-xs tracking-[0.16em] uppercase font-medium hover:bg-[#1F3A2A]/20"
          >
            Keep my data
          </button>
        ) : (
          <button
            type="button"
            onClick={requestDelete}
            disabled={requesting}
            data-testid={`${testid}-request-delete`}
            className="inline-flex items-center gap-2 rounded-full px-4 py-2 bg-[#FFB800]/30 text-[#9C7826] text-xs tracking-[0.16em] uppercase font-medium hover:bg-[#FFB800]/50"
          >
            <Trash2 size={13} strokeWidth={1.7} />
            Schedule removal
          </button>
        )}
      </div>
    </section>
  );
}
